import { inject } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { AuthService } from 'src/app/services/http/auth.service';
import { AppConfig } from 'src/app/app.config';
import { Features } from 'src/app/models/features';
import { Store } from '@ngrx/store';
import * as AuthActions from 'src/app/store/auth/actions/auth.actions';
import { IAppState } from 'src/app/store/state/app.state';
import { IJwtoken } from 'src/app/models/jwtoken';
import { environment } from 'src/environments/environment';

interface TokenData {
    accessToken: string | null;
    refreshToken: string | null;
    expiresIn: string | null;
}

const getTokenDataFromCookies = (authService: AuthService): TokenData => ({
    accessToken: authService.getCookie('identityServerAccessToken'),
    refreshToken: authService.getCookie('identityServerRefreshToken'),
    expiresIn: authService.getCookie('identityServerExpiresIn')
});

const createJwtToken = (tokenData: TokenData): IJwtoken => ({
    access_token: tokenData.accessToken!,
    refresh_token: tokenData.refreshToken || '',
    expires_in: parseInt(tokenData.expiresIn || '0', 10),
    token_type: 'Bearer'
});

const constructLoginUrl = (loginFeUrl: string, state: RouterStateSnapshot, appName: string): string => {
    const baseUrl = `${window.location.protocol}//${window.location.host}`;
    const fullPath = `${baseUrl}${state.url}`;
    const encodedUrl = encodeURIComponent(fullPath);
    return `${loginFeUrl}?return_url=${encodedUrl}&appName=${appName}`;
};

const handleValidToken = async (
    tokenData: TokenData,
    store: Store<IAppState>,
    router: Router
): Promise<boolean> => {
    try {
        const jwtToken = createJwtToken(tokenData);
        localStorage.setItem('token', JSON.stringify(jwtToken));
        store.dispatch(new AuthActions.UniversalLoginAuthenticate(jwtToken));
        const navigationSuccess = await router.navigate(['/feature/dashboard']);

        if (!navigationSuccess) {
            console.error('Navigation to dashboard failed');
            return true; // Fall back to traditional login if navigation fails
        }

        return false;
    } catch (error) {
        console.error('Error handling valid token:', error);
        return true; // Allow fallback to traditional login on error
    }
};

export const universalLoginGuard = async (
    state: RouterStateSnapshot
) => {
    const authService = inject(AuthService);
    const appConfig = inject(AppConfig);
    const store = inject(Store<IAppState>);
    const router = inject(Router);

    const useUniversalLogin = appConfig.getSection(Features.UNIVERSALLOGIN)?.enabled ?? false;

    if (!useUniversalLogin) {
        return true;
    }

    const tokenData = getTokenDataFromCookies(authService);

    if (tokenData.accessToken) {
        return handleValidToken(tokenData, store, router);
    }

    const loginFeUrl = appConfig.getEnvironmentConfig().loginFeUrl;
    const appName = environment.appName;
    const loginUrl = constructLoginUrl(loginFeUrl, state, appName);
    window.location.replace(loginUrl);
    return false;
};